import { stripSearchParams, type SearchMiddleware } from "@tanstack/react-router";
import type { operations } from "@/api/schema";
import type { SearchObjekt } from "./search-params";

/*
 * Das Filtermodell der sechs Such-Endpunkte.
 *
 * ═══ WARUM DAS EINE GETEILTE DATEI IST ═══════════════════════════════════
 *
 * Gemessen, nicht vermutet: `/api/v1/suche`, `/api/v1/verkaufssuche`,
 * `/api/v1/export/csv` und `/api/v1/export/verkauf/csv` nehmen die
 * IDENTISCHEN neunzehn Query-Parameter — Name für Name, Mehrfachigkeit für
 * Mehrfachigkeit. Der Go-Port führt sie deshalb auch nur einmal, als
 * `search.QueryVertrag`, und leitet die Huma-Parameter daraus ab „statt 19
 * Namen ein zweites Mal zu tippen".
 *
 * Zwei Sichten und zwei Export-Knöpfe, die jeweils ihre eigene Fassung dieser
 * Liste bauen, wären vier Wahrheiten über denselben Vertrag. Sie laufen
 * auseinander, sobald ein Parameter dazukommt — und zwar still, weil jede für
 * sich compiliert. Hier steht sie einmal.
 *
 * ═══ DIE DREI SORTEN, UND WARUM SIE SICH UNTERSCHEIDEN ═══════════════════
 *
 * Sie sind keine Geschmacksfrage, sondern stehen so im Go-Vertrag
 * (`internal/search/query.go`), und der Unterschied ist bedeutungstragend:
 *
 *   LISTEN     `[]string`  — wiederholter Parameter (`?loc=A&loc=B`).
 *                            Der LEERE Wert ist ein Marker: `loc=` heißt
 *                            „alle Standorte" und ist NICHT dasselbe wie ein
 *                            fehlendes `loc`. Als `[""]` ausgedrückt.
 *   SCHALTER   `*string`   — hier macht FEHLEN vs. LEER einen Unterschied.
 *                            Der Kommentar am Go-Typ nennt den Fall wörtlich:
 *                            „`?slow=1` ohne nn schaltet nn aus, `?slow=1&nn=`
 *                            nicht." Ein blosser String kann das nicht
 *                            ausdrücken — deshalb `string | undefined`.
 *   EINZELWERTE `string`   — gewöhnliche Einzelwerte. Serverseitig sind ""
 *                            und „fehlt" hier dasselbe (`v.Get`), also wird
 *                            leer weggelassen; das hält geteilte URLs kurz.
 *
 * ═══ WORAN DIE TYPEN HÄNGEN ══════════════════════════════════════════════
 *
 * `SucheQuery` ist NICHT hier getippt, sondern aus dem Generat gezogen:
 * `operations["getSuche"]["parameters"]["query"]`. Benennt der Go-Port einen
 * Parameter um, bricht nach `pnpm codegen` der Build an `alsQuery` — statt
 * dass die Suche still ungefiltert liefe. Genau die Zusage, die dieses Repo
 * für Request-Bodys teuer gelernt hat, hier für die Query.
 *
 * Dass alle sechs Endpunkte wirklich dieselben Parameter führen, ist keine
 * Behauptung dieses Kommentars: `suche-filter.test.ts` liest sie aus
 * `docs/openapi.json` und vergleicht sie gegen die drei Listen unten.
 */

/**
 * Die zehn Parameter, die als WIEDERHOLTER Wert hinausgehen.
 *
 * Reihenfolge wie im Go-Vertrag — sie bestimmt die Reihenfolge in der URL und
 * damit, wie eine geteilte Suche aussieht.
 */
export const LISTEN_PARAMETER = [
  "loc",
  "og",
  "g",
  "ug",
  "xg",
  "xug",
  "alt",
  "hl",
  "ff",
  "rut",
] as const;

/**
 * Die fünf, bei denen FEHLEN und LEER verschiedene Dinge sind.
 *
 * Im Go-Vertrag sind das genau die `*string`-Felder.
 */
export const SCHALTER_PARAMETER = ["vkmin", "vkmax", "slow", "nn", "bestand"] as const;

/** Die vier gewöhnlichen Einzelwerte; leer heißt hier „nicht gesetzt". */
export const EINZEL_PARAMETER = ["q", "bw", "sort", "page"] as const;

export type ListenParameter = (typeof LISTEN_PARAMETER)[number];
export type SchalterParameter = (typeof SCHALTER_PARAMETER)[number];
export type EinzelParameter = (typeof EINZEL_PARAMETER)[number];

/**
 * Der Filterzustand einer Such-Sicht.
 *
 * ALLE Felder sind vorhanden — keines ist optional. Das ist Absicht: ein
 * optionales Feld erlaubt es, beim Bauen eines Filters eines zu vergessen,
 * und der Compiler schwiege dazu. So ist ein vergessenes Feld ein Fehler.
 * Wer „nicht gesetzt" meint, schreibt `[]`, `undefined` oder `""` — je nach
 * Sorte, und die Sorte steht oben.
 */
export type SucheFilter = Readonly<Record<ListenParameter, readonly string[]>> &
  Readonly<Record<SchalterParameter, string | undefined>> &
  Readonly<Record<EinzelParameter, string>>;

/**
 * Die Query-Form, die `api.GET` erwartet — aus dem Generat.
 *
 * Alle sechs Endpunkte führen dieselbe; `getSuche` steht stellvertretend, und
 * der Test hält fest, dass die anderen fünf nicht davon abweichen.
 */
export type SucheQuery = NonNullable<operations["getSuche"]["parameters"]["query"]>;

/**
 * Der leere Filter — die Suche ohne jede Einschränkung.
 *
 * Nicht `{}` mit Aufweichung des Typs, sondern jedes Feld ausgeschrieben:
 * kommt ein Parameter hinzu, ist diese Konstante die erste Stelle, die es
 * merkt.
 */
export const LEERER_FILTER: SucheFilter = {
  loc: [],
  og: [],
  g: [],
  ug: [],
  xg: [],
  xug: [],
  alt: [],
  hl: [],
  ff: [],
  rut: [],
  vkmin: undefined,
  vkmax: undefined,
  slow: undefined,
  nn: undefined,
  bestand: undefined,
  q: "",
  bw: "",
  sort: "",
  page: "",
};

/**
 * Filter → Query-Objekt für `api.GET(…, { params: { query } })`.
 *
 * Weggelassen wird, was nichts aussagt: leere Listen und leere Einzelwerte.
 * NICHT weggelassen wird ein Schalter, der als leerer String dasteht — er ist
 * der Unterschied zwischen `?slow=1` und `?slow=1&nn=`, und dieser
 * Unterschied gehört dem Server.
 *
 * Die Reihenfolge der Schlüssel ist die Einfüge-Reihenfolge: erst die Listen,
 * dann die Schalter, dann die Einzelwerte. `stringifySearch` schreibt sie
 * genau so in die URL, und `openapi-fetch` hält sie ebenfalls (nachgewiesen
 * in `api/query-serialisierung.test.ts`, „hält die Parameter-Reihenfolge der
 * Aufrufer-Angabe").
 */
export function alsQuery(filter: SucheFilter): SucheQuery {
  const query: Record<string, string | string[]> = {};

  for (const name of LISTEN_PARAMETER) {
    const werte = filter[name];
    // Leere Liste heißt „kein Filter" und darf keinen Parameter erzeugen —
    // ein `?loc=` wäre der MARKER und damit eine andere Aussage.
    if (werte.length > 0) query[name] = [...werte];
  }
  for (const name of SCHALTER_PARAMETER) {
    const wert = filter[name];
    // Nur `undefined` fällt weg. `""` ist ein gesetzter Schalter.
    if (wert !== undefined) query[name] = wert;
  }
  for (const name of EINZEL_PARAMETER) {
    const wert = filter[name];
    if (wert !== "") query[name] = wert;
  }

  return query;
}

/**
 * Filter → Search-Objekt für den Router (`Link`, `navigate`).
 *
 * Dieselbe Auswahl wie `alsQuery`, nur die Zielform ist eine andere:
 * `stringifySearch` aus `search-params.ts` macht daraus den Query-String, und
 * dessen Kodierung ist gegen den Golden-Master gepinnt. Deshalb wird hier
 * NICHT selbst serialisiert.
 *
 * ═══ Diese Auswahl allein HÄLT NICHT — der Router hebt sie wieder auf ════
 *
 * Zwei Durchgänge lang stand hier die Zusage, ein Aufruf über `alsSearch`
 * halte `?q=&bw=&sort=&page=` aus der Adresse. Gemessen war sie falsch, und
 * zwar an genau EINER Stelle im Router — `Transitioner.tsx`, beim Mount:
 *
 *     const nextLocation = router.buildLocation({
 *       to: location.pathname, search: true, …, _includeValidateSearch: true,
 *     });
 *     if (trimPathRight(location.publicHref) !== trimPathRight(nextLocation.publicHref))
 *       router.commitLocation({ ...nextLocation, replace: true, … });
 *
 * `_includeValidateSearch: true` lässt `validateSearch` laufen und mischt
 * dessen VOLLSTÄNDIGEN Filter über das, was der Aufrufer ausgewählt hat. Die
 * vier leeren Einzelwerte sind damit wieder da, `stringifySearch` behält sie
 * (dort ist `""` der `loc=`-Marker), die beiden Adressen sind verschieden —
 * und der Router schreibt die Adresse um. Ohne dass jemand einen Filter
 * angefasst hätte.
 *
 * Derselbe Griff sitzt auf dem Klick-Weg: `navigate({ search: alsSearch(…) })`
 * geht durch dieselbe Middleware-Kette, und die `validate`-Stufe mischt die
 * neunzehn Felder NACH der Auswahl des Aufrufers wieder darauf. Gemessen:
 *
 *     /suche                  →  /suche?q=&bw=&sort=&page=
 *     navigate({loc:[…]})     →  /suche?loc=Wismar&q=&bw=&sort=&page=
 *
 * Deshalb steht die Auswahl ein zweites Mal als [OHNE_LEERE_EINZELWERTE] in
 * den Routen. `alsSearch` bleibt trotzdem: es ist die Auswahl für JEDEN
 * Aufrufer, auch für die, die keine Route sind (Drill-Down-Links,
 * `exportCsvUrl`), und für sie greift keine Router-Middleware.
 */
export function alsSearch(filter: SucheFilter): SearchObjekt {
  return alsQuery(filter);
}

/**
 * Die Werte, bei denen ein Einzelwert in der ADRESSE nichts aussagt.
 *
 * `Record<EinzelParameter, "">` und nicht `Partial<…>`: der Typ erzwingt
 * Vollständigkeit. Kommt ein fünfter Einzelwert dazu, ist diese Konstante ein
 * Compilerfehler, bis jemand ihn hinschreibt — dieselbe Zusage wie bei
 * `LEERER_FILTER`, und aus demselben Grund.
 */
export const LEERE_EINZELWERTE: Readonly<Record<EinzelParameter, "">> = {
  q: "",
  bw: "",
  sort: "",
  page: "",
};

/**
 * Die Middleware, die die Adresse von leeren Einzelwerten frei hält.
 *
 * ═══ Warum das eine ROUTEN-Option ist und nichts in `main.tsx` ═══════════
 *
 * Naheliegend wäre, `stringifySearch` das leere Feld wegwerfen zu lassen. Das
 * wäre der teuerste Fehler, den man hier machen kann: dieser Serialisierer
 * kennt die drei Sorten NICHT, und für die Listen ist der leere Wert der
 * bedeutungstragende Marker — `loc=` heißt „alle Standorte". Ein Fix dort
 * nähme ihn mit. `createRouter` hat auch gar keinen Schalter dafür; der Haken
 * sitzt je Route (`search.middlewares`), und nur dort ist die Sorte bekannt.
 *
 * ═══ Warum die Gegenrichtung von selbst hält ═════════════════════════════
 *
 * `stripSearchParams` vergleicht mit `deepEqual(wert, vorgabe)` und wirft nur
 * weg, was GLEICH ist. Gemessen an der Vergleichsfunktion selbst:
 *
 *     deepEqual("",   "")  →  true    (`q=`   fällt weg)
 *     deepEqual([""], "")  →  false   (`loc=` bleibt stehen)
 *
 * Dazu kommt der zweite, unabhängige Grund: `loc` steht in
 * `LEERE_EINZELWERTE` überhaupt nicht. Beide Richtungen sind in
 * `suche-filter.test.ts` gepinnt — eine Reparatur, die den Marker mitnimmt,
 * wäre schlimmer als der Befund, den sie behebt.
 *
 * Unbekannte Parameter bleiben ebenfalls unangetastet (gemessen: `?fremd=x`
 * überlebt). Ein Lesezeichen aus dem Altsystem verliert hier nichts.
 */
export const OHNE_LEERE_EINZELWERTE: SearchMiddleware<SucheFilter> =
  stripSearchParams(LEERE_EINZELWERTE);

/**
 * Ein roher Wert aus der URL → Liste.
 *
 * `parseSearch` ist nicht typstabil: ein einzelnes Vorkommen kommt als
 * String, mehrere als Array. Das ist keine Schwäche dort, sondern die
 * Eigenschaft der Kodierung — `?loc=A` trägt nicht, ob ein Array gemeint war.
 * Die Vereinheitlichung gehört hierher, wo der erwartete Typ je Parameter
 * bekannt ist.
 *
 * `""` bleibt erhalten: es ist der „alle"-Marker, nicht Abwesenheit.
 */
function alsListe(roh: unknown): readonly string[] {
  if (typeof roh === "string") return [roh];
  if (Array.isArray(roh)) return roh.filter((w): w is string => typeof w === "string");
  return [];
}

/**
 * Ein roher Wert → Schalter.
 *
 * Mehrfach angegeben gewinnt das ERSTE Vorkommen — genau wie serverseitig
 * (`einzel()` in `internal/search/query.go` nimmt `werte[0]`). Eine URL von
 * Hand mit `?nn=0&nn=1` beantworten Client und Server damit gleich.
 */
function alsSchalter(roh: unknown): string | undefined {
  if (typeof roh === "string") return roh;
  if (Array.isArray(roh) && typeof roh[0] === "string") return roh[0];
  return undefined;
}

/** Ein roher Wert → Einzelwert; fehlend wird zu `""`. */
function alsEinzel(roh: unknown): string {
  return alsSchalter(roh) ?? "";
}

/**
 * Rohe Search-Params → Filter.
 *
 * Das ist die `validateSearch`-Hälfte: was aus der URL kommt, ist
 * unvertraut — jeder Wert kann fehlen, doppelt oder vom falschen Typ sein.
 * Unbekannte Parameter werden schlicht ignoriert; sie gehören womöglich einer
 * anderen Sicht oder einem Lesezeichen aus dem Altsystem.
 */
export function ausSearch(roh: Record<string, unknown>): SucheFilter {
  const filter: Record<string, unknown> = {};
  for (const name of LISTEN_PARAMETER) filter[name] = alsListe(roh[name]);
  for (const name of SCHALTER_PARAMETER) filter[name] = alsSchalter(roh[name]);
  for (const name of EINZEL_PARAMETER) filter[name] = alsEinzel(roh[name]);
  return filter as SucheFilter;
}

/**
 * Trägt dieser Filter überhaupt eine Einschränkung?
 *
 * `page` zählt NICHT mit: auf Seite 3 einer ungefilterten Liste zu stehen ist
 * keine Einschränkung, und ein „Filter zurücksetzen"-Knopf, der wegen der
 * Seitenzahl erscheint, wäre irreführend.
 */
export function hatEinschraenkung(filter: SucheFilter): boolean {
  if (LISTEN_PARAMETER.some((name) => filter[name].length > 0)) return true;
  if (SCHALTER_PARAMETER.some((name) => filter[name] !== undefined)) return true;
  return false;
}

/** Freitext, Wertmodus und Sortierung bleiben beim Abwählen der Filter erhalten. */
export function ohneEinschraenkungen(filter: SucheFilter): SucheFilter {
  return { ...LEERER_FILTER, q: filter.q, bw: filter.bw, sort: filter.sort };
}

/** Wie SearchParams auf dem Server: ein fehlendes nn lässt slow=1 zu. */
export function auchNachgekauft(filter: SucheFilter): boolean {
  return filter.nn === "0" || (filter.nn === undefined && filter.slow === "1");
}

export function slowAktiv(filter: SucheFilter): boolean {
  return filter.slow === "1" && auchNachgekauft(filter);
}
